/**
 * A team's mark when there is no logo media on file: the team's own
 * abbreviation in a fixed height, variable width plate, never a broken image.
 *
 * Fixed height, no fixed width. A square cannot hold a four character
 * abbreviation at any readable type size (ISSUE-541), so the box is fitted to
 * the text instead. `md`'s min-w-20 keeps every two or three character value at
 * one width, which is what keeps the away and home names in `GameDetailHeader`
 * aligned. Raised from min-w-16 under Inter (ISSUE-542): `WWW` at 700 16px
 * measures 67px in Inter, past the old 64px floor, and
 * tests/e2e/src/app/teamMarkGeometry.e2e.ts asserts the equal-width invariant
 * that would have caught it. `sm` sits in a single horizontal bar with no
 * column, so its minimum width is just its own height.
 *
 * `GameDetailHeader` uses this. ISSUE-182 owns folding this,
 * `ScoreRow`'s inline mark and `FanTeamPlate` into one component.
 *
 * `lg` is the 56px team profile header size added by ISSUE-182, and it keeps
 * the fixed height variable width rule so a four character abbreviation
 * still fits.
 */
import { cn } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

const SIZE_CLASSES: Record<"sm" | "md" | "lg", string> = {
  sm: "h-6 min-w-6 max-w-32 overflow-hidden px-1 text-caption",
  md: "h-9 min-w-20 max-w-32 overflow-hidden px-2 text-body-strong",
  lg: "h-14 min-w-14 max-w-40 overflow-hidden px-2 text-h2",
};

export const TeamMark = ({
  abbreviation,
  logoUrl,
  size,
}: {
  name: string;
  abbreviation: string;
  /** The resolved mark URL from the server, or null when this team has no
   *  mark on file. The plate below is the fallback (ISSUE-902 R5). */
  logoUrl?: string | null;
  size: "sm" | "md" | "lg";
}): ReactNode => {
  // The branch is on the URL alone and never on a media status field, the
  // rule `apps/admin/src/features/artwork/MarkCell.tsx` states in its own
  // header: media-api returns a variant only for a published object, so the
  // publish gate is inherited rather than re-checked here. The outer span
  // keeps the same size classes in both branches and only its fill differs,
  // and the image is positioned over the box, so loading it can move nothing
  // (ISSUE-1087). That is what keeps ISSUE-541's and ISSUE-542's width
  // invariants true. The logo sits on a light disc in both themes so a dark
  // logo stays readable at night.
  const [hasFailed, setHasFailed] = useState(false);
  useEffect(() => {
    setHasFailed(false);
  }, [logoUrl]);
  const imageUrl =
    typeof logoUrl === "string" && logoUrl !== "" && !hasFailed
      ? logoUrl
      : null;

  return (
    <span
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-full font-bold",
        imageUrl !== null
          ? "bg-mark-logo-surface"
          : "bg-ink-secondary text-surface",
        SIZE_CLASSES[size],
      )}
      aria-hidden="true"
      data-testid="team-mark"
    >
      {imageUrl !== null ? (
        <img
          src={imageUrl}
          alt=""
          data-testid="team-mark-image"
          className="absolute inset-0 h-full w-full object-contain p-1"
          onError={() => {
            setHasFailed(true);
          }}
        />
      ) : (
        abbreviation
      )}
    </span>
  );
};
